import React, { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';
import LoadingButton from './LoadingButton';

interface BookScannerProps {
  onImagesScanned: (books: Array<{title: string, author: string}>) => void;
  onScanningStart: () => void;
  onScanningComplete: () => void;
}

const BookScanner: React.FC<BookScannerProps> = ({
  onImagesScanned,
  onScanningStart,
  onScanningComplete
}) => {
  const { showError } = useToast();
  const { isLoading, withLoading } = useLoading();
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const MAX_IMAGES = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // Validate file
  const validateFile = (file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      showError('Erreur', 'Seules les images sont autorisées');
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      showError('Erreur', 'La taille de l\'image ne doit pas dépasser 5MB');
      return false;
    }
    return true;
  };

  // Add images to selection
  const addImages = useCallback((files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files).filter(validateFile);
    
    if (selectedImages.length + newFiles.length > MAX_IMAGES) {
      showError('Erreur', `Maximum ${MAX_IMAGES} images autorisées`);
      return;
    }

    setSelectedImages(prev => [...prev, ...newFiles]);
  }, [selectedImages.length, showError]);

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addImages(e.target.files);
    if (e.target) e.target.value = ''; // Reset input
  };

  // Handle camera input change
  const handleCameraChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addImages(e.target.files);
    if (e.target) e.target.value = ''; // Reset input
  };

  // Remove image from selection
  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    addImages(e.dataTransfer.files);
  };

  // Resize image for better performance
  const resizeImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const maxWidth = 2048;
        const maxHeight = 2048;
        
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(resizedFile);
          } else {
            resolve(file);
          }
        }, 'image/jpeg', 0.85);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Scan images with OpenAI
  const scanImages = withLoading('scan-images', async () => {
    if (selectedImages.length === 0) {
      showError('Erreur', 'Veuillez sélectionner au moins une image');
      return;
    }

    onScanningStart();

    try {
      // Resize images for better performance
      const resizedImages = await Promise.all(
        selectedImages.map(resizeImage)
      );

      const formData = new FormData();
      resizedImages.forEach((file, index) => {
        formData.append('images', file);
      });

      const response = await fetch('/api/books/scan-images', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de l\'analyse des images');
      }

      const data = await response.json();
      onImagesScanned(data.books);
      
    } catch (error: any) {
      showError('Erreur', error.message || 'Erreur lors de l\'analyse des images');
    } finally {
      onScanningComplete();
    }
  });

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          isDragOver
            ? 'border-forest-400 bg-forest-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-gray-400" />
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Glissez vos images ici
            </h3>
            <p className="text-gray-600 mb-4">
              ou utilisez les boutons ci-dessous pour sélectionner des images
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 bg-forest-600 text-white rounded-lg hover:bg-forest-700 transition-colors"
            >
              <Upload className="h-4 w-4 mr-2" />
              Choisir des images
            </button>
            
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Camera className="h-4 w-4 mr-2" />
              Prendre une photo
            </button>
          </div>

          <p className="text-sm text-gray-500">
            Maximum {MAX_IMAGES} images • JPG, PNG, WebP • 5MB max par image
          </p>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCameraChange}
          className="hidden"
        />
      </div>

      {/* Selected Images Preview */}
      {selectedImages.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900">
              Images sélectionnées ({selectedImages.length}/{MAX_IMAGES})
            </h4>
            <button
              onClick={() => setSelectedImages([])}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Tout supprimer
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {selectedImages.map((file, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
                <div className="mt-1 text-xs text-gray-500 truncate">
                  {file.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scan Button */}
      {selectedImages.length > 0 && (
        <div className="flex justify-center">
          <LoadingButton
            onClick={scanImages}
            loading={isLoading('scan-images')}
            loadingText="Analyse en cours..."
            disabled={selectedImages.length === 0}
            className="px-8 py-3"
            variant="primary"
            size="lg"
          >
            <Camera className="h-5 w-5 mr-2" />
            Analyser les images ({selectedImages.length})
          </LoadingButton>
        </div>
      )}

      {/* Tips */}
      <div className="bg-forest-50 border border-forest-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-forest-700 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-forest-800">
            <h4 className="font-semibold mb-2">Conseils pour de meilleurs résultats :</h4>
            <ul className="space-y-1 text-forest-700">
              <li>• Photographiez les livres de face, avec un bon éclairage</li>
              <li>• Assurez-vous que les titres et auteurs sont lisibles</li>
              <li>• Évitez les reflets et les ombres sur les couvertures</li>
              <li>• Plusieurs livres peuvent être photographiés ensemble</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookScanner;
